import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ActivityCard from './ActivityCard';

/**
 * Draggable Activity List Component
 * Provides drag-and-drop reordering of activities using @dnd-kit
 */

// Sortable wrapper for ActivityCard
const SortableActivityCard = ({
  activity,
  index,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  subsessions,
  onAssignToSubsession,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ActivityCard
        activity={activity}
        index={index}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onPreview={onPreview}
        subsessions={subsessions}
        onAssignToSubsession={onAssignToSubsession}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

const DraggableActivityList = ({
  activities,
  subsessions,
  onReorder,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  onAssignToSubsession,
  emptyState,
}) => {
  const [activeId, setActiveId] = useState(null);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance to activate
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = activities.findIndex((item) => item.id === active.id);
      const newIndex = activities.findIndex((item) => item.id === over.id);

      const reorderedActivities = arrayMove(activities, oldIndex, newIndex);

      if (onReorder) {
        onReorder(reorderedActivities);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get the currently dragging activity for the overlay
  const activeActivity = activities.find((activity) => activity.id === activeId);

  // Empty state
  if (!activities || activities.length === 0) {
    return (
      emptyState || (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
          <div className="w-16 h-16 mx-auto mb-4 border-2 border-neutral-400 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-secondary-900 mb-2">No Activities Added</h3>
          <p className="text-neutral-600 mb-6">
            Start building your session by adding activities from templates or creating custom ones.
          </p>
        </div>
      )
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={activities} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {activities.map((activity, index) => (
            <SortableActivityCard
              key={activity.id}
              activity={activity}
              index={index}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onPreview={onPreview}
              subsessions={subsessions}
              onAssignToSubsession={onAssignToSubsession}
            />
          ))}
        </div>
      </SortableContext>

      {/* Drag Overlay - Shows the item being dragged */}
      <DragOverlay>
        {activeId && activeActivity ? (
          <div className="opacity-90 rotate-2">
            <ActivityCard
              activity={activeActivity}
              index={activities.findIndex((a) => a.id === activeId)}
              onEdit={() => {}}
              onDelete={() => {}}
              onDuplicate={() => {}}
              onPreview={() => {}}
              isDragging={true}
              dragHandleProps={{}}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableActivityList;
